import { StowagePlan } from 'src/types/ViewModel/Stowage/StowagePlan';
import { Tank } from 'src/types/ViewModel/Vessel/Tank';
import { RotationPortCall } from 'src/types/ViewModel/Stowage/Rotation/RotationPortCall';
import { BlockedTank } from 'src/types/ViewModel/Stowage/BlockedTank';
import { RotationBerthCall } from 'src/types/ViewModel/Stowage/Rotation/RotationBerthCall';
import { UUID } from 'crypto';

export const isTankBlocked = (
    stowagePlan: StowagePlan,
    tank: Tank,
    currentPortCall: RotationPortCall,
    currentBerthCall: RotationBerthCall,
    rotationTree: RotationPortCall[]
): boolean => {
    const blockedTank = stowagePlan?.blockedTanks?.find((t) => t.tank && t.tank.tankId === tank.tankId);
    return blockedTank ? isNodeInRange(rotationTree, blockedTank, currentPortCall, currentBerthCall) : false;
};

const isNodeInRange = (
    rotationTree: RotationPortCall[],
    blockedTank: BlockedTank,
    currentPortCall: RotationPortCall | null,
    currentBerthCall: RotationBerthCall | null
): boolean => {
    return (
        isAfterUpperBoundary(rotationTree, blockedTank, currentPortCall, currentBerthCall) &&
        isBeforeLowerBoundary(rotationTree, blockedTank, currentPortCall, currentBerthCall)
    );
};

const getPortCallIndex = (
    rotationTree: RotationPortCall[],
    portCallGuid: UUID) => rotationTree.findIndex((portCall) => portCall.portCallGuid === portCallGuid);

const getBerthCallIndex = (
    rotationTree: RotationPortCall[],
    berthCallGuid: UUID) => rotationTree.flatMap((pc) => pc.berthCalls).findIndex((berthCall) => berthCall.berthCallGuid === berthCallGuid);

const isAfterUpperBoundary = (
    rotationTree: RotationPortCall[],
    blockedTank: BlockedTank,
    currentPortCall: RotationPortCall | null,
    currentBerthCall: RotationBerthCall | null
): boolean => {
    if (!currentPortCall) return false;

    const blockedPortCallIndex = getPortCallIndex(rotationTree, blockedTank.fromPortCall?.portCallGuid);
    const currentPortCallIndex = getPortCallIndex(rotationTree, currentPortCall.portCallGuid);

    if (currentPortCallIndex < blockedPortCallIndex) return false;
    if (currentPortCallIndex > blockedPortCallIndex) return true;

    if (!currentBerthCall || !blockedTank.fromBerthCall) return true;

    const blockedBerthCallIndex = getBerthCallIndex(rotationTree, blockedTank.fromBerthCall.berthCallGuid);
    const currentBerthCallIndex = getBerthCallIndex(rotationTree, currentBerthCall.berthCallGuid);

    return currentBerthCallIndex >= blockedBerthCallIndex;
};

const isBeforeLowerBoundary = (
    rotationTree: RotationPortCall[],
    blockedTank: BlockedTank,
    currentPortCall: RotationPortCall | null,
    currentBerthCall: RotationBerthCall | null
): boolean => {
    if (!currentPortCall) return false;

    if (!blockedTank.toPortCall) {
        return true;
    }

    const blockedPortCallIndex = getPortCallIndex(rotationTree, blockedTank.toPortCall.portCallGuid);
    const currentPortCallIndex = getPortCallIndex(rotationTree, currentPortCall.portCallGuid);

    if (currentPortCallIndex < blockedPortCallIndex) return true;
    if (currentPortCallIndex > blockedPortCallIndex) return false;

    if (!currentBerthCall || !blockedTank.toBerthCall) return true;

    const blockedBerthCallIndex = getBerthCallIndex(rotationTree, blockedTank.toBerthCall.berthCallGuid);
    const currentBerthCallIndex = getBerthCallIndex(rotationTree, currentBerthCall.berthCallGuid);

    return currentBerthCallIndex <= blockedBerthCallIndex;
};

export const hasRotationItemBlockedTank = (
    stowagePlan: StowagePlan,
    rotationPortCall: RotationPortCall | null,
    rotationBerthCall: RotationBerthCall
) => {
    const _hasRotationItemBlockedTank = stowagePlan?.blockedTanks?.some((t) => {
        if (rotationPortCall && !rotationBerthCall) {
            return t.fromPortCallGuid === rotationPortCall.portCallGuid || t.toPortCallGuid === rotationPortCall.portCallGuid;
        } else if (rotationBerthCall) {
            return t.fromBerthCallGuid === rotationBerthCall.berthCallGuid || t.toBerthCallGuid === rotationBerthCall.berthCallGuid;
        }
        return false;
    });

    return _hasRotationItemBlockedTank;
};
